// Git forge sync per workspace (/v1/workspaces/{id}/git). A connected
// workspace mirrors a repository on a git forge: the server replaces the file
// tree on every sync (push webhook or manual trigger) and file writes are
// locked while connected (409 workspace_git_locked). When the status names a
// repository, the connection belongs to that repository as a whole: connect
// turns it into a mirror of the forge, disconnect turns it back into one hosted
// here and keeps its history, and branch is its default branch. The access
// token is write-only; webhookUrl and webhookSecret appear only for
// maintainers.

import { getJSON, sendJSON, request } from "./http.js";

const BASE = "/v1/workspaces";

/** GitSyncStatus is the sync lifecycle; terminal states are succeeded/failed. */
export type GitSyncStatus = "pending" | "syncing" | "succeeded" | "failed";

export interface GitStatus {
  connected: boolean;
  /** The repository the workspace belongs to, when it has one. */
  repository?: string;
  // Present when connected:
  repoUrl?: string;
  /** The branch followed; for a repository its default branch, connected or not. */
  branch?: string;
  /** Whether an access token is stored (its value is never returned). */
  hasToken?: boolean;
  lastSyncSha?: string;
  lastSyncAt?: string;
  lastSyncStatus?: GitSyncStatus;
  /** Present (non-empty) when lastSyncStatus is "failed". */
  lastSyncError?: string;
  /** The build kicked off by the last successful sync, if any. */
  lastBuildId?: string;
  // Maintainer-only:
  webhookUrl?: string;
  webhookSecret?: string;
}

/** getGitStatus returns the workspace's git connection status (viewer+). */
export function getGitStatus(id: string): Promise<GitStatus> {
  return getJSON<GitStatus>(`${BASE}/${encodeURIComponent(id)}/git`);
}

/**
 * connectGit connects the workspace to a repository, or updates an existing
 * connection (upsert, maintainer+). An empty/absent token keeps the stored one
 * on update. A sync is triggered; the response is the maintainer status payload
 * including webhookUrl + webhookSecret.
 */
export function connectGit(
  id: string,
  req: { repoUrl: string; branch?: string; token?: string },
): Promise<GitStatus> {
  return sendJSON<GitStatus>(`${BASE}/${encodeURIComponent(id)}/git`, "POST", req);
}

/** syncGitNow queues an async sync of the connected branch (member+); poll getGitStatus for the outcome. */
export async function syncGitNow(id: string): Promise<void> {
  await sendJSON(`${BASE}/${encodeURIComponent(id)}/git/sync`, "POST", {});
}

/** disconnectGit removes the connection (maintainer+); the synced tree stays and editing unlocks. */
export async function disconnectGit(id: string): Promise<void> {
  await request(`${BASE}/${encodeURIComponent(id)}/git`, { method: "DELETE" });
}
